import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { db } from "../firebase";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  increment,
  collection,
  getDocs,
} from "firebase/firestore";

// ─── Types ─────────────────────────────────────────────
type RateType =
  | "perNightPerPerson"
  | "perHourPerPerson"
  | "perHour"
  | "perNight"
  | "perWeek"
  | "perMonth"
  | "perYear";

// ─── Utility Functions ─────────────────────────────────
export const calculateHostEarnings = (total: number) => {
  let hostFeePercentage = 0.00;
  const hostsFee = total * hostFeePercentage;
  const hostProfit = parseFloat((total - hostsFee).toFixed(2));
  return { hostProfit, hostsFee };
};

export const calculateGuestServiceFee = (subtotal: number): number => {
  if (subtotal > 5000) return +(subtotal * 0.1).toFixed(2);
  if (subtotal > 1000) return +(subtotal * 0.09).toFixed(2);
  if (subtotal > 500)  return +(subtotal * 0.08).toFixed(2);
  if (subtotal > 100)  return +(subtotal * 0.07).toFixed(2);
  return +(subtotal * 0.06).toFixed(2);
};

// ─── Helper: nights → billing units ───────────────────
const nightsToBillingUnits = (nights: number, rateType: RateType): number => {
  switch (rateType) {
    case "perWeek":  return Math.ceil(nights / 7);
    case "perMonth": return Math.ceil(nights / 30);
    case "perYear":  return Math.ceil(nights / 365);
    default:         return nights;
  }
};

// ─── Earnings Calculation ─────────────────────────────
export const calculateShortstayEarnings = createAsyncThunk(
  "shortstayServiceFees/calculateShortstayEarnings",
  async ({
    rate,
    rateType,
    reservedDuration = 0,
    totalHours = 0,
    adults = 1,
    extraCharges = 0,
    discountApplied = {},
    creditsAvailable = 0,
  }: {
    rate: number;
    rateType: RateType;
    reservedDuration?: number;
    totalHours?: number;
    adults?: number;
    extraCharges?: number;
    discountApplied?: {
      is5?: boolean;
      is10?: boolean;
      is25?: boolean;
      is50?: boolean;
      is80?: boolean;
    };
    creditsAvailable?: number;
  }) => {
    const safeAdults = Math.max(1, adults);

    let discountPercent = 0;
    if (discountApplied.is80)      discountPercent = 0.8;
    else if (discountApplied.is50) discountPercent = 0.5;
    else if (discountApplied.is25) discountPercent = 0.25;
    else if (discountApplied.is10) discountPercent = 0.1;
    else if (discountApplied.is5)  discountPercent = 0.05;

    let baseStayCost = 0;

    switch (rateType) {
      case "perNightPerPerson":
        baseStayCost = rate * reservedDuration * safeAdults;
        break;
      case "perHourPerPerson":
        baseStayCost = rate * totalHours * safeAdults;
        break;
      case "perHour":
        baseStayCost = rate * totalHours;
        break;
      case "perNight":
        baseStayCost = rate * reservedDuration;
        break;
      case "perWeek":
        // reservedDuration is in nights → convert to weeks (partial weeks rounded up)
        baseStayCost = rate * Math.ceil(reservedDuration / 7);
        break;
      case "perMonth":
        // reservedDuration is in nights → convert to months (partial months rounded up)
        baseStayCost = rate * Math.ceil(reservedDuration / 30);
        break;
      case "perYear":
        // reservedDuration is in nights → convert to years (partial years rounded up)
        baseStayCost = rate * Math.ceil(reservedDuration / 365);
        break;
    }

    const discountedStayCost = baseStayCost * (1 - discountPercent);
    const totalStayCost = discountedStayCost + extraCharges;

    const guestsFee = calculateGuestServiceFee(totalStayCost);
    const creditsUsed = Math.min(creditsAvailable, totalStayCost);
    const stayAfterCredits = totalStayCost - creditsUsed;

    const totalCharged = +(stayAfterCredits + guestsFee).toFixed(2);
    const { hostProfit, hostsFee } = calculateHostEarnings(totalStayCost);
    const platformProfit = +(guestsFee + hostsFee).toFixed(2);

    return {
      rateType,
      baseStayCost,
      discountedStayCost,
      totalStayCost,
      guestsFee,
      hostProfit,
      hostsFee,
      platformProfit,
      creditsAvailable,
      totalCharged,
      creditsUsed,
      fullCost: totalStayCost + guestsFee,
    };
  }
);

// ─── Batch Final Prices ───────────────────────────────
export const calculateFinalPricesForAllListings = createAsyncThunk(
  "shortstayServiceFees/calculateFinalPricesForAllListings",
  async (
    {
      reservedDuration,
      totalHours = 0,
      adults,
    }: {
      reservedDuration: number;
      totalHours?: number;
      adults: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const snapshot = await getDocs(collection(db, "shortstays"));

      const results: Record<string, { totalCharged: number; fullCost: number }> = {};

      snapshot.forEach((docSnap) => {
        const listing = docSnap.data();
        const listingId = docSnap.id;

        const rate: number = listing.rate || 0;
        const rateType: RateType = listing.rateType || "perNight";
        const discounts = listing.discounts || {};
        const safeAdults = Math.max(1, adults);

        let discountPercent = 0;
        if (discounts.is80)      discountPercent = 0.8;
        else if (discounts.is50) discountPercent = 0.5;
        else if (discounts.is25) discountPercent = 0.25;
        else if (discounts.is10) discountPercent = 0.1;
        else if (discounts.is5)  discountPercent = 0.05;

        // ⏱️ fallback: if hourly listing but no hours provided, estimate from nights
        let effectiveHours = totalHours;
        if (
          (rateType === "perHour" || rateType === "perHourPerPerson") &&
          effectiveHours === 0
        ) {
          effectiveHours = reservedDuration * 24;
        }

        let baseStayCost = 0;

        switch (rateType) {
          case "perNightPerPerson":
            baseStayCost = rate * reservedDuration * safeAdults;
            break;
          case "perHourPerPerson":
            baseStayCost = rate * effectiveHours * safeAdults;
            break;
          case "perHour":
            baseStayCost = rate * effectiveHours;
            break;
          case "perNight":
            baseStayCost = rate * reservedDuration;
            break;
          case "perWeek":
            baseStayCost = rate * Math.ceil(reservedDuration / 7);
            break;
          case "perMonth":
            baseStayCost = rate * Math.ceil(reservedDuration / 30);
            break;
          case "perYear":
            baseStayCost = rate * Math.ceil(reservedDuration / 365);
            break;
          default:
            baseStayCost = rate * reservedDuration;
            break;
        }

        const discountedStayCost = baseStayCost * (1 - discountPercent);
        const guestsFee = calculateGuestServiceFee(discountedStayCost);

        results[listingId] = {
          totalCharged: +(discountedStayCost + guestsFee).toFixed(2),
          fullCost: discountedStayCost + guestsFee,
        };
      });

      return results;
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

// ─── Slice ─────────────────────────────────────────────
const shortstayServiceFeeSlice = createSlice({
  name: "shortstayServiceFees",
  initialState: {
    totalCharged: 0,
    platformProfit: 0,
    creditsAvailable: 0,
    hostsFee: 0,
    baseStayCost: 0,
    discountedStayCost: 0,
    totalStayCost: 0,
    guestsFee: 0,
    hostProfit: 0,
    creditsUsed: 0,
    fullCost: 0,
    finalPricesByListing: {} as Record<
      string,
      { totalCharged: number; fullCost: number }
    >,
    monthlyEarningsData: {} as Record<string, number>,
    monthlyEarnings: {},
  },
  reducers: {
    resetEarnings: (state) => {
      Object.assign(state, shortstayServiceFeeSlice.getInitialState());
    },
    clearFinalPrices: (state) => {
      state.finalPricesByListing = {};
    },
  },
  extraReducers: (builder) => {
    builder.addCase(calculateShortstayEarnings.fulfilled, (state, action) => {
      Object.assign(state, action.payload);
    });
    builder.addCase(
      calculateFinalPricesForAllListings.fulfilled,
      (state, action) => {
        state.finalPricesByListing = action.payload;
      }
    );
  },
});

export const { resetEarnings, clearFinalPrices } =
  shortstayServiceFeeSlice.actions;
export default shortstayServiceFeeSlice.reducer;